import { showNotification } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext } from 'react';

import { KYCContext } from '~/components/providers/KYCProvider';
import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import type { UseKYCParams } from '~/domains/auth/components/SignUpSteps/types';
import { useKYCStatus } from '~/hooks/useKYCStatus';
import { postKycVerify } from '~/services/kyc/kyc.service.api';
import { SocureKycStatus } from '~/services/users/types';

// @todo - review next sprint 12/13/24
const useKYC = ({
  formValues,
  onComplete,
}: UseKYCParams & { onComplete?: (status: SocureKycStatus) => void }) => {
  const { firstName, lastName, address, city, state, zip, country, phone, dob } = formValues;
  const { user, refreshUser, refreshUserDetails, refreshUserSsnStatus } = useContext(UserContext);
  const { completeVerification } = useContext(KYCContext);
  const { refetch: refreshKycStatus } = useKYCStatus(user?.id);

  const payload = {
    firstName,
    lastName,
    address,
    city,
    state,
    country,
    zip,
    mobileNumber: `+1${phone}`,
    email: user?.email,
    customerUserId: user?.id,
    dateOfBirth: {
      month: Number(dob.month),
      day: Number(dob.day),
      year: Number(dob.year),
    },
  };

  const {
    mutate: handleKyc,
    isLoading,
    isSuccess,
  } = useMutation({
    mutationKey: ['verification'],
    mutationFn: () => {
      // @todo - should this change to a different event? V2?
      reportEvent('KYC > Submit', {
        step: 2,
      });

      return postKycVerify({ ...payload, sessionVersion: 3 });
    },
    onSuccess: (res) => {
      const status = res.data.status;

      if (status === SocureKycStatus.RESUBMIT) {
        showNotification({
          title: 'Address Error',
          message: 'Please ensure that your information is correct and resubmit your data',
          color: 'yellow',
          autoClose: 2500,
        });

        reportEvent('KYC > Rejected', {
          reason: 'Address Error',
        });
      }

      // Genuine in-session KYC-form result — mark the completion (on ACCEPT) that gates
      // the return-to-origin redirect (EDGE-32717).
      completeVerification(status);
      refreshUserDetails();
      refreshUserSsnStatus();
      refreshKycStatus();
      // Refreshes the token and re-fetches the auth user, so the KYC names replace the
      // sign-up names in UserContext and session storage (EDGE-32008).
      refreshUser();

      onComplete?.(status);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error.response?.data?.message;
      reportEvent('KYC > Rejected', {
        reason: 'KYC Verification Error',
        errorMessage,
      });
      showNotification({
        title: 'Verification Error',
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  return { handleKyc, isLoading, isSuccess };
};

export default useKYC;
